import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a ChunkLoadError (happens after deployments)
    const isChunkError = error?.name === 'ChunkLoadError' || 
                         error?.message?.includes('Loading chunk') ||
                         error?.message?.includes('ChunkLoadError');
    
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    // Filter out PostHog errors
    if (error.message && error.message.includes('PerformanceServerTiming')) {
      this.setState({ hasError: false });
      return;
    }
    
    // Auto-refresh for ChunkLoadError (only once per session)
    const isChunkError = error?.name === 'ChunkLoadError' || 
                         error?.message?.includes('Loading chunk') ||
                         error?.message?.includes('ChunkLoadError');
    
    if (isChunkError) {
      const lastChunkRefresh = sessionStorage.getItem('lastChunkRefresh');
      const now = Date.now();
      
      // Only auto-refresh if we haven't refreshed in the last 10 seconds
      if (!lastChunkRefresh || (now - parseInt(lastChunkRefresh)) > 10000) {
        sessionStorage.setItem('lastChunkRefresh', now.toString());
        console.log('ChunkLoadError detected, auto-refreshing...');
        window.location.reload();
        return;
      }
    }
    
    // Store error info for display
    this.setState({ errorInfo });
    
    // Log errors with full details
    console.error('Error caught by boundary:', error);
    console.error('Error stack:', error?.stack);
    console.error('Component stack:', errorInfo?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Special UI for ChunkLoadError
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Update Available
              </h2>
              <p className="text-slate-600 mb-4">
                A new version of the app is available. Please refresh to get the latest updates.
              </p>
              <button
                onClick={() => {
                  // Clear any cached chunks and force reload
                  if ('caches' in window) {
                    caches.keys().then(names => {
                      names.forEach(name => caches.delete(name));
                    });
                  }
                  window.location.reload(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-1"
              >
                Refresh Now
              </button>
            </div>
          </div>
        );
      }
      
      return (
        <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-slate-600 mb-4">
              We encountered an unexpected error. Please try refreshing the page.
            </p>
            {/* Show error details in development or for debugging */}
            {this.state.error && (
              <details className="text-left mb-4 text-xs bg-slate-50 rounded-lg p-3">
                <summary className="cursor-pointer text-slate-500 font-medium">Error Details (for support)</summary>
                <pre className="mt-2 text-red-600 overflow-auto max-h-32 whitespace-pre-wrap">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="bg-[#E8430A] hover:bg-[#E8430A] text-white rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-orange-500/30 transition-all duration-300 transform hover:-translate-y-1"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
